import { orderBy } from "lodash";

import { DEFAULT_SORTING_OPTIONS } from "./constants";

type BinnedResults<T> = { [key: string]: T[] };
export type SortDirection = "asc" | "desc";

type BinDepartureOptions<T, R = T[], F = R> = {
  binningStrategy: (values: T[]) => BinnedResults<T>;
  filterStrategy?:
    | ((values: T[], bin_key: string | number, bin_values: T[]) => F)
    | undefined;
  orderByOptions?: Partial<{
    [key in keyof T]: SortDirection;
  }>;
};

export type BinningStrategy<T> = BinDepartureOptions<T>["binningStrategy"];
export type BinningFilterStrategy<T, R = T[]> = BinDepartureOptions<
  T,
  R
>["filterStrategy"];
export type BinningOrderByOptions<T> = NonNullable<
  BinDepartureOptions<T>["orderByOptions"]
>;

type LodashOrderByOptions<T> = [Array<keyof T>, Array<SortDirection>];

function formatOrderByOptions<T = Record<string, SortDirection>>(
  order_by_options: T | undefined | null
) {
  if (!order_by_options) {
    return null;
  }
  return Object.entries(order_by_options).reduce(
    (acc, [key, value]) => {
      acc[0].push(key as keyof T);
      acc[1].push(value as SortDirection);
      return acc;
    },
    [[], []] as LodashOrderByOptions<T>
  );
}

function buildOrderResults<T = {}>(
  order_by_options: LodashOrderByOptions<any> | undefined | null
) {
  return (values: T[]) => {
    if (!order_by_options) {
      return values;
    }
    return orderBy(values, ...order_by_options);
  };
}

export function binResults<T = {}, R = T[], F = R[]>(
  values: T[],
  bin_options: BinDepartureOptions<T, R>
): F {
  const {
    binningStrategy,
    filterStrategy = values => values,
    orderByOptions = DEFAULT_SORTING_OPTIONS
  } = bin_options;
  const lodash_order_by_options = formatOrderByOptions(orderByOptions);
  const orderResults = buildOrderResults<T>(lodash_order_by_options);
  const bins = binningStrategy(values);
  const entries = Object.entries(bins);

  return entries.map(([bin_key, bin]) => {
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    return filterStrategy(orderResults(bin), bin_key, bins[bin_key]);
  }) as F;
}

export type KeyGetter<T> = (value: T) => string | number;

export function buildBinningBase<T = {}>(getKey: KeyGetter<T>) {
  return {
    bin(values: T[]) {
      const bins: BinnedResults<T> = {};
      values.forEach(value => {
        const binKey = getKey(value);
        bins[binKey] = bins[binKey] || [];
        bins[binKey].push(value);
      });
      return bins;
    },
    getKey
  };
}
